import { vec2 } from 'gl-matrix';
import { useCallback, useEffect, useRef } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import {
    currentDoseState,
    dicomDvhCurvesState,
    doseDataState,
    selectedDoseRangeState,
} from '~/state/atoms/dose/doseAtoms';
import { environmentState } from '~/state/atoms/engineAtoms';
import transformDoseMatrixToDoseData from '~/utils/transforms/doseMatrixTransforms';
import { DoseTargetData } from '~/utils/transforms/doseTargetsTransforms';

export default function useDose(overrideDoseSource?: DoseMatrix) {
    const environment = useRecoilValue(environmentState);
    const selectedDoseSource = useRecoilValue(currentDoseState);
    const resolvedDoseSource = overrideDoseSource || selectedDoseSource;
    const [doseViewData, setDoseViewData] = useRecoilState(doseDataState);
    const setDicomDvhCurves = useSetRecoilState(dicomDvhCurvesState);
    const setSelectedDoseRange = useSetRecoilState(selectedDoseRangeState);

    const currentSopUidRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (!resolvedDoseSource && doseViewData) {
            setDoseViewData(undefined);
            setDicomDvhCurves([]);
            currentSopUidRef.current = undefined;
        }
    }, [resolvedDoseSource, doseViewData, setDoseViewData, setDicomDvhCurves]);

    useEffect(() => {
        if (!resolvedDoseSource) {
            return;
        }

        const sopUid = resolvedDoseSource.dicomUid;
        if (sopUid === currentSopUidRef.current) {
            return;
        }

        currentSopUidRef.current = sopUid;
        setDoseViewData(transformDoseMatrixToDoseData(resolvedDoseSource));
        setDicomDvhCurves(resolvedDoseSource.buildDicomDvhCurves());

        // Restore the per-dose range into the dedicated atom so the
        // colorwash slider reflects the correct selection after switching.
        setSelectedDoseRange(resolvedDoseSource.selectedRange);

        if (environment) {
            environment.updateSelectedDoseRange(
                resolvedDoseSource.selectedRange
            );
        }
    }, [
        resolvedDoseSource,
        setDoseViewData,
        setDicomDvhCurves,
        setSelectedDoseRange,
        environment,
    ]);

    const setNormalizationValue = useCallback(
        (normalizationValueGy: number) => {
            if (resolvedDoseSource) {
                resolvedDoseSource.normalizationValueGy = normalizationValueGy;
                setDoseViewData((prev) =>
                    prev ? { ...prev, normalizationValueGy } : prev
                );
            }
        },
        [resolvedDoseSource, setDoseViewData]
    );

    const setDoseScale = useCallback(
        (doseScale: number) => {
            if (resolvedDoseSource) {
                resolvedDoseSource.doseScale = doseScale;
                setDoseViewData(
                    transformDoseMatrixToDoseData(resolvedDoseSource)
                );
                setDicomDvhCurves(resolvedDoseSource.buildDicomDvhCurves());
            }
        },
        [resolvedDoseSource, setDoseViewData, setDicomDvhCurves]
    );

    const setDoseRange = useCallback(
        (selectedRange: vec2) => {
            if (resolvedDoseSource) {
                // Persist on domain object for dose-switching survival
                resolvedDoseSource.selectedRange = selectedRange;
            }
            // Only the dedicated atom is updated — doseDataState is untouched,
            // so metadata consumers do not re-render during slider drags.
            setSelectedDoseRange(selectedRange);
            if (environment) {
                environment.updateSelectedDoseRange(selectedRange);
            }
        },
        [resolvedDoseSource, environment, setSelectedDoseRange]
    );

    const setTargets = useCallback(
        (targets: DoseTargetData[]) => {
            if (resolvedDoseSource) {
                resolvedDoseSource.targets = targets;
                setDoseViewData((prev) => (prev ? { ...prev, targets } : prev));
            }
        },
        [resolvedDoseSource, setDoseViewData]
    );

    return {
        dose: doseViewData,
        setNormalizationValue,
        setDoseScale,
        setDoseRange,
        setTargets,
    };
}
